import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/lib/api';
import { hexToHsl, luminance } from '@/shared/lib/color';

export interface Branding {
  name: string;
  logoUrl: string | null;
  primaryColor: string | null;
}

export const BRANDING_KEY = ['branding'] as const;

/** Public white-label branding of the organization (`GET /organization/branding`). */
export function useBranding() {
  return useQuery({
    queryKey: BRANDING_KEY,
    queryFn: () => api.get<Branding>('/organization/branding'),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
    meta: { silent: true },
  });
}

/** Applies the organization's primary colour as CSS tokens (`--brand-h/s/l`, `--brand-fg`). */
export function useApplyBranding(): void {
  const { data } = useBranding();
  const color = data?.primaryColor ?? null;

  useEffect(() => {
    const root = document.documentElement.style;
    const hsl = color ? hexToHsl(color) : null;
    if (!hsl || !color) {
      ['--brand-h', '--brand-s', '--brand-l', '--brand-fg'].forEach((v) => root.removeProperty(v));
      return;
    }
    // Clamp lightness so the accent stays usable on both light and dark surfaces.
    const l = Math.min(Math.max(hsl.l, 32), 58);
    root.setProperty('--brand-h', String(hsl.h));
    root.setProperty('--brand-s', `${hsl.s}%`);
    root.setProperty('--brand-l', `${l}%`);
    root.setProperty('--brand-fg', luminance(color) > 0.45 ? '240 10% 9%' : '0 0% 100%');
  }, [color]);
}
